import { useEffect, useMemo, useRef } from "react";

import { useSetAtom } from "jotai";

import {
    type ExclusiveLensStatus,
    type GoogleProtobufTimestamp,
    type LensCreatorPayoutEligibility,
    type LensSourceApplication,
    type SurfaceType,
    type Visibility,
} from "../../gql-generated/types";
import { type AssistantLensDetails, type AssistantLensLifetimeStats } from "../model";
import { activeAssistantLensDetailsAtom, assistantLensStatsByKeyAtom } from "../state";

export interface AssistantLensDetailsInput {
    lensKey: string;
    name: string;
    publicationStatus: string;
    displayVisibility: Visibility;
    exclusiveLensStatus: ExclusiveLensStatus;
    lensCreatorPayoutEligibility: LensCreatorPayoutEligibility;
    surfaceTypes: readonly SurfaceType[];
    sourceApplication?: LensSourceApplication | null;
    createdAt?: GoogleProtobufTimestamp | null;
    updatedAt?: GoogleProtobufTimestamp | null;
    tags: readonly string[];
}

interface AssistantOrganicStatInput {
    label: string;
    value: string;
}

interface AssistantPayoutStatInput {
    value: string;
}

export interface AssistantLensStatsInput {
    lensKey: string;
    organicStats: readonly AssistantOrganicStatInput[];
    organicLoading: boolean;
    organicError: boolean;
    payoutStats: {
        subscriptionsEarned: AssistantPayoutStatInput;
        audience: AssistantPayoutStatInput;
        score: AssistantPayoutStatInput;
        payout: AssistantPayoutStatInput;
        loading?: boolean;
    };
}

const lensPlusPayoutsStatusMap: Record<ExclusiveLensStatus, string> = {
    EXCLUSIVE_LENS_STATUS_EXCLUSIVE: "Enrolled and exclusive",
    EXCLUSIVE_LENS_STATUS_ELIGIBLE: "Pending approval",
    EXCLUSIVE_LENS_STATUS_INELIGIBLE: "Ineligible",
    EXCLUSIVE_LENS_STATUS_REMOVED: "Removed from Lens+ Program",
    EXCLUSIVE_LENS_STATUS_UNSET: "Not enrolled",
};

const topPerformerPayoutsStatusMap: Record<LensCreatorPayoutEligibility, string> = {
    LENS_CREATOR_PAYOUT_ELIGIBILITY_ELIGIBLE: "Enrolled",
    LENS_CREATOR_PAYOUT_ELIGIBILITY_PENDING: "Pending review",
    LENS_CREATOR_PAYOUT_ELIGIBILITY_INELIGIBLE: "Ineligible",
    LENS_CREATOR_PAYOUT_ELIGIBILITY_UNSET: "Not enrolled",
};

const visibilityMap: Record<Visibility, string> = {
    VISIBILITY_PUBLIC: "Public",
    VISIBILITY_HIDDEN: "Hidden",
    VISIBILITY_OFFLINE: "Offline",
    VISIBILITY_DONT_PUBLISH_TO_SNAPCHAT: "Do not publish to Snapchat",
    VISIBILITY_UNSET: "",
};

const surfaceTypeMap: Record<SurfaceType, string | null> = {
    SURFACE_TYPE_SNAPCHAT: "Snapchat",
    SURFACE_TYPE_SPECTACLES: "Spectacles",
    SURFACE_TYPE_UNSET: null,
};

const sourceApplicationMap: Record<LensSourceApplication, string | null> = {
    SOURCE_APPLICATION_BBG_FINALIZATION_TOOL: "BBG Finalization Tool",
    SOURCE_APPLICATION_LENS_APPLIER: "Lens Applier",
    SOURCE_APPLICATION_LENS_STUDIO: "Lens Studio",
    SOURCE_APPLICATION_LENS_STUDIO_MOBILE: "Lens Studio Mobile",
    SOURCE_APPLICATION_LENS_STUDIO_WEB: "Lens Studio Web",
    SOURCE_APPLICATION_LENS_WEB_BUILDER: "Lens Web Builder",
    SOURCE_APPLICATION_MY_AI_CHATBOT: "My AI Chatbot",
    SOURCE_APPLICATION_SNAP_AI: "Snap AI",
    SOURCE_APPLICATION_UNSET: null,
};

const timestampToIsoString = (timestamp: GoogleProtobufTimestamp | null | undefined): string | null => {
    if (!timestamp) return null;
    const date = new Date(timestamp.seconds * 1000 + timestamp.nanos / 1e6);
    return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

const findOrganicStatValue = (stats: readonly AssistantOrganicStatInput[], label: string): string | undefined =>
    stats.find((stat) => stat.label === label)?.value;

const normalizeStatValue = (value: string | undefined, unavailable: boolean): string | undefined => {
    if (unavailable || value === undefined) return undefined;
    const normalizedValue = value.trim();
    return normalizedValue === "" || normalizedValue === "-" || normalizedValue === "N/A" || normalizedValue === "Error"
        ? undefined
        : value;
};

const areArraysEqual = <Value>(left: readonly Value[], right: readonly Value[]): boolean =>
    left.length === right.length && left.every((value, index) => value === right[index]);

const areOrganicStatsEqual = (
    left: readonly AssistantOrganicStatInput[],
    right: readonly AssistantOrganicStatInput[],
): boolean =>
    left.length === right.length &&
    left.every((stat, index) => stat.label === right[index]?.label && stat.value === right[index]?.value);

const useSemanticallyStableValue = <Value>(value: Value, isEqual: (left: Value, right: Value) => boolean): Value => {
    const stableValue = useRef(value);
    if (!isEqual(stableValue.current, value)) {
        stableValue.current = value;
    }
    return stableValue.current;
};

export const normalizeAssistantLensDetails = ({
    lensKey,
    name,
    publicationStatus,
    displayVisibility,
    exclusiveLensStatus,
    lensCreatorPayoutEligibility,
    surfaceTypes,
    sourceApplication,
    createdAt,
    updatedAt,
    tags,
}: AssistantLensDetailsInput): AssistantLensDetails => {
    const surfaceType = surfaceTypes
        .map((type) => surfaceTypeMap[type])
        .filter((type): type is string => type !== null)
        .join(", ");

    return {
        lensKey,
        name,
        publicationStatus,
        visibility: visibilityMap[displayVisibility],
        lensPlusPayoutsStatus: lensPlusPayoutsStatusMap[exclusiveLensStatus],
        topPerformerPayoutsStatus: topPerformerPayoutsStatusMap[lensCreatorPayoutEligibility],
        surfaceType: surfaceType || null,
        sourceApplication: sourceApplication ? sourceApplicationMap[sourceApplication] : null,
        createdAt: timestampToIsoString(createdAt),
        updatedAt: timestampToIsoString(updatedAt),
        tags: tags.length > 0 ? [...tags] : null,
    };
};

export const normalizeAssistantLensLifetimeStats = ({
    organicStats,
    organicLoading,
    organicError,
    payoutStats,
}: AssistantLensStatsInput): AssistantLensLifetimeStats => {
    const organicUnavailable = organicLoading || organicError;
    const payoutUnavailable = payoutStats.loading ?? false;

    return {
        views: normalizeStatValue(findOrganicStatValue(organicStats, "Lens Views"), organicUnavailable),
        plays: normalizeStatValue(findOrganicStatValue(organicStats, "Lens Plays"), organicUnavailable),
        shares: normalizeStatValue(findOrganicStatValue(organicStats, "Lens Shares"), organicUnavailable),
        lensPlusSubscriptionsEarned: normalizeStatValue(payoutStats.subscriptionsEarned.value, payoutUnavailable),
        totalLensPlusAudience: normalizeStatValue(payoutStats.audience.value, payoutUnavailable),
        lensPlusScore: normalizeStatValue(payoutStats.score.value, payoutUnavailable),
        payout: normalizeStatValue(payoutStats.payout.value, payoutUnavailable),
    };
};

export const useAssistantLensDetailsContext = ({
    lensKey,
    name,
    publicationStatus,
    displayVisibility,
    exclusiveLensStatus,
    lensCreatorPayoutEligibility,
    surfaceTypes,
    sourceApplication,
    createdAt,
    updatedAt,
    tags,
}: AssistantLensDetailsInput): void => {
    const setActiveLensDetails = useSetAtom(activeAssistantLensDetailsAtom);
    const stableSurfaceTypes = useSemanticallyStableValue(surfaceTypes, areArraysEqual);
    const stableTags = useSemanticallyStableValue(tags, areArraysEqual);
    const createdAtSeconds = createdAt?.seconds;
    const createdAtNanos = createdAt?.nanos;
    const updatedAtSeconds = updatedAt?.seconds;
    const updatedAtNanos = updatedAt?.nanos;
    const details = useMemo(
        () =>
            normalizeAssistantLensDetails({
                lensKey,
                name,
                publicationStatus,
                displayVisibility,
                exclusiveLensStatus,
                lensCreatorPayoutEligibility,
                surfaceTypes: stableSurfaceTypes,
                sourceApplication,
                createdAt:
                    createdAtSeconds === undefined || createdAtNanos === undefined
                        ? null
                        : { seconds: createdAtSeconds, nanos: createdAtNanos },
                updatedAt:
                    updatedAtSeconds === undefined || updatedAtNanos === undefined
                        ? null
                        : { seconds: updatedAtSeconds, nanos: updatedAtNanos },
                tags: stableTags,
            }),
        [
            lensKey,
            name,
            publicationStatus,
            displayVisibility,
            exclusiveLensStatus,
            lensCreatorPayoutEligibility,
            stableSurfaceTypes,
            sourceApplication,
            stableTags,
            createdAtSeconds,
            createdAtNanos,
            updatedAtSeconds,
            updatedAtNanos,
        ],
    );

    useEffect(() => {
        setActiveLensDetails(details);
    }, [details, setActiveLensDetails]);

    useEffect(
        // Only clear when this Lens is still active, so navigating to another
        // Lens's details doesn't get wiped by this page's unmount cleanup.
        () => () => {
            setActiveLensDetails((current) => (current?.lensKey === lensKey ? null : current));
        },
        [lensKey, setActiveLensDetails],
    );
};

export const useAssistantLensStatsContext = ({
    lensKey,
    organicStats,
    organicLoading,
    organicError,
    payoutStats,
}: AssistantLensStatsInput): void => {
    const setLensStatsByKey = useSetAtom(assistantLensStatsByKeyAtom);
    const stableOrganicStats = useSemanticallyStableValue(organicStats, areOrganicStatsEqual);
    const subscriptionsEarned = payoutStats.subscriptionsEarned.value;
    const audience = payoutStats.audience.value;
    const score = payoutStats.score.value;
    const payout = payoutStats.payout.value;
    const payoutLoading = payoutStats.loading;
    const lifetimeStats = useMemo(
        () =>
            normalizeAssistantLensLifetimeStats({
                lensKey,
                organicStats: stableOrganicStats,
                organicLoading,
                organicError,
                payoutStats: {
                    subscriptionsEarned: { value: subscriptionsEarned },
                    audience: { value: audience },
                    score: { value: score },
                    payout: { value: payout },
                    loading: payoutLoading,
                },
            }),
        [
            lensKey,
            stableOrganicStats,
            organicLoading,
            organicError,
            subscriptionsEarned,
            audience,
            score,
            payout,
            payoutLoading,
        ],
    );

    useEffect(() => {
        setLensStatsByKey((current) => ({ ...current, [lensKey]: lifetimeStats }));
    }, [lensKey, lifetimeStats, setLensStatsByKey]);

    useEffect(
        () => () => {
            setLensStatsByKey((current) => {
                if (!(lensKey in current)) return current;
                return Object.fromEntries(Object.entries(current).filter(([key]) => key !== lensKey));
            });
        },
        [lensKey, setLensStatsByKey],
    );
};
